/**
 * MaskReslice — shared reslice infrastructure for the mask volume.
 *
 * Centralises the reslice parameters so the on-screen mask in the orthogonal
 * CPR viewport, the floating diameter label, and the stent-planning panel all
 * produce byte-identical 2D slices.
 */

import vtkImageReslice from 'vtk.js/Sources/Imaging/Core/ImageReslice';

import { buildResliceAxesFromFrame } from './CenterlineUtils';

/** Output spacing multiplier vs. axial volume's min spacing (0.5 = 2x resolution). */
export const DIAMETER_RESLICE_SPACING_FACTOR = 0.5;
/** Half-extent (mm) of the resliced 2D plane on each side of the centerline point. */
export const DIAMETER_RESLICE_HALF_EXTENT_MM = 60;
/**
 * vtkImageReslice interpolation modes.  0 = Nearest, 1 = Linear, 2 = Cubic.
 */
export const ResliceInterpolationMode = Object.freeze({
    NEAREST: 0,
    LINEAR: 1,
    CUBIC: 2,
});
/** Axial-volume background label that resolves to "outside the volume." */
const AXIAL_EMPTY_SEGMENTATION_LABEL = -1024;

/**
 * Build a freshly configured reslice instance with the parameters expected by
 * the diameter pipeline (linear interpolation, transparent background outside
 * the volume).
 */
export function newReslice() {
    return vtkImageReslice.newInstance({
        outputDimensionality: 2,
        interpolationMode: ResliceInterpolationMode.LINEAR,
        autoCropOutput: false,
        transformInputSampling: true,
        backgroundColor: [AXIAL_EMPTY_SEGMENTATION_LABEL, 0, 0, 0],
    });
}

/**
 * Configure a vtkImageReslice instance with the mask reslice parameters
 * for a given centerline frame, and run it.  Caller owns the reslice instance
 * (allows reuse to avoid VTK churn).
 *
 * @returns the reslice output (a 2D vtkImageData), or null if the inputs are
 * not viable.
 */
export function runReslice(reslice, maskVolume, frame) {
    if (!reslice || !maskVolume || !frame) return null;

    const spacing = maskVolume.getSpacing();
    const minSpacing = Math.min(spacing[0], spacing[1], spacing[2]);
    const outSpacing = minSpacing * DIAMETER_RESLICE_SPACING_FACTOR;
    const halfPixels = Math.ceil(DIAMETER_RESLICE_HALF_EXTENT_MM / outSpacing);

    reslice.setInputData(maskVolume);
    reslice.setResliceAxes(buildResliceAxesFromFrame(frame));
    reslice.setOutputSpacing([outSpacing, outSpacing, outSpacing]);
    reslice.setOutputOrigin([
        -DIAMETER_RESLICE_HALF_EXTENT_MM,
        -DIAMETER_RESLICE_HALF_EXTENT_MM,
        0,
    ]);
    reslice.setOutputExtent([0, 2 * halfPixels, 0, 2 * halfPixels, 0, 0]);
    reslice.setOutputDimensionality(2);
    reslice.update();

    return reslice.getOutputData();
}

/**
 * Walk the reslice mask output and rewrite its scalars so the background
 * becomes 0 and every other value becomes 1.  Mutates in place.
 */
export function normalizeReslicedMaskToBinary(reslicedMask) {
    if (!reslicedMask) return;
    const scalars = reslicedMask.getPointData().getScalars();
    const data = scalars.getData();
    for (let i = 0; i < data.length; i++) {
        data[i] = data[i] !== 0 ? 1 : 0;
    }
    scalars.modified();
    reslicedMask.modified();
}

/**
 * Overwrite the resliced mask's scalar buffer with a previously-stored painted
 * slice buffer.  Buffer dimensions are guaranteed equal because both the live
 * paint and the cache use the same reslice parameters defined in this module.
 */
export function applyStoredSliceBuffer(reslicedMask, storedBuffer) {
    if (!reslicedMask || !storedBuffer) return;
    const scalars = reslicedMask.getPointData().getScalars();
    const target = scalars.getData();
    if (target.length !== storedBuffer.length) {
        // Dimensions diverged, likely a different spacing factor was used
        // when the slice was painted.  Skip the overlay rather than corrupt
        // the mask; the live (unmodified) reslice still produces a usable
        // diameter.
        console.warn(
            'MaskReslice: stored slice buffer length',
            storedBuffer.length,
            'does not match reslice output',
            target.length,
            '— skipping overlay'
        );
        return;
    }
    target.set(storedBuffer);
    scalars.modified();
    reslicedMask.modified();
}
